import { Theme } from 'styled-components';
import { NextRouter } from 'next/router';
import languages from './languages.json';
import supportedLanguages from './supportedLanguages';
import { USER_CONTINENT, USER_COUNTRY } from './constants';
import { captureExceptionWithFullyStory } from './utils';
import { flagMap } from './flagMap';
import { ILocales } from './types';

export const site = {
  locale: 'en',
  countries: {},
};
const momentCodes = {
  en: 'en-au',
  'zh-hans': 'zh-cn',
  'zh-hant': 'zh-hk',
};

export function detectCookies(): boolean {
  try {
    const test = 'test';
    localStorage.setItem(test, test);
    sessionStorage.setItem(test, test);
    if (localStorage.getItem(test) !== sessionStorage.getItem(test)) {
      return false;
    }
    localStorage.removeItem(test);
    sessionStorage.removeItem(test);
    return navigator.cookieEnabled;
  } catch {
    return false;
  }
}
export const getUserCountry = (): string => (detectCookies() && sessionStorage.getItem(USER_COUNTRY)) || 'AU';

export const getUserContinent = (): string | null => (detectCookies() ? sessionStorage.getItem(USER_CONTINENT) : null);

export const isUSEnglish = (locale: string) =>
  locale === 'en' && (getUserCountry() === 'US' || getUserContinent() === 'SA');

export const locales: Record<string, ILocales> = Object.keys(languages).reduce((availableLocales, locale) => {
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const { code, name, nameLocal, bidi: isRTL } = languages[locale];
  const flagCode = isUSEnglish(locale) ? 'en-us' : locale;
  const Flag = flagMap[flagCode as keyof typeof flagMap];

  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  availableLocales[locale] = {
    code,
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    momentCode: momentCodes[locale] || code,
    name,
    nameLocal,
    isRTL,
    Flag,
  };

  return availableLocales;
}, {});

function getTranslatedLanguage(browserLanguage: string, languageArray: string[]) {
  let translatedLanguage = languageArray.find((lang) => browserLanguage.startsWith(lang));
  const USLanguagePresent = languageArray.find((lang) => lang === 'en-us');
  if (
    translatedLanguage &&
    translatedLanguage.startsWith('en') &&
    USLanguagePresent &&
    (getUserCountry() === 'US' || getUserContinent() === 'SA')
  ) {
    translatedLanguage = 'en-us';
  } else if (!translatedLanguage) {
    translatedLanguage = site.locale;
  }
  return translatedLanguage;
}

function getBrowserLanguage() {
  const browserLanguage =
    (navigator.languages && navigator.languages[0]) || navigator.language || (navigator as any).userLanguage;
  switch (browserLanguage) {
    case 'zh-TW':
    case 'zh-HK':
      return 'zh-hant';
    case 'zh':
    case 'zh-CN':
      return 'zh-hans';
    default:
      return browserLanguage;
  }
}

export function detectLanguage(languagesArg: any = languages, router?: NextRouter): void {
  const translatedLanguages = Object.keys(languagesArg);
  const supportedLanguageKeys = Object.keys(supportedLanguages);
  const { pathname } = window.location;
  const pathLanguage = pathname.split('/')[1];

  const redirectToNewURL = (newPathLanguage: string) => {
    let { href } = window.location;
    const slashIndex = href.indexOf('/', 8);
    if (supportedLanguageKeys.includes(pathLanguage)) {
      href = href.replace(`/${pathLanguage}`, '');
    }
    const newURL = `${href.slice(0, slashIndex)}/${newPathLanguage}${href.slice(slashIndex)}`.replace(/(\/)+$/, '');
    if (router) {
      router.replace(newURL);
    } else {
      window.location.replace(newURL);
    }
  };

  if (!pathLanguage || !translatedLanguages.includes(pathLanguage)) {
    const translatedLanguage = getTranslatedLanguage(getBrowserLanguage(), translatedLanguages);
    redirectToNewURL(translatedLanguage);
  } else {
    site.locale = pathLanguage;
  }
}

export const getCountryName = (countryCode: string): string => {
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  if (site.countries[countryCode]) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return site.countries[countryCode].name;
  }
  captureExceptionWithFullyStory(new Error(`${countryCode} country code not found in Countries object from XC.js`));
  return countryCode;
};
export const getCountryNames = (countryCodesArray: string[]): string =>
  countryCodesArray.map((code) => getCountryName(code)).join(', ');

export const detectIE = (): boolean => {
  const ua = window.navigator.userAgent;

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older
    return true;
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11
    return true;
  }

  // other browser
  return false;
};

export const detectLowerCaseINS = () => {
  const { origin, pathname, search } = window.location;
  const INSregex = /([a-z0-9]{5}-){2}(ins|INS)(\/|$)/;
  if (INSregex.exec(pathname)) {
    const newPathname = pathname.replace(INSregex, (ins) => ins.toUpperCase());
    const newUrl = origin + newPathname + search;
    window.location.replace(newUrl);
  }
};

const overrideFontFamilies = (fontFamily: string, theme: Theme) => {
  const fontFamilies = {
    base: `${fontFamily}, ${theme.fontFamilies.base}`,
    headings: `${fontFamily}, ${theme.fontFamilies.headings}`,
    sanSerif: `${fontFamily}, ${theme.fontFamilies.sanSerif}`,
    serif: `${fontFamily}, ${theme.fontFamilies.serif}`,
    monospace: `${fontFamily}, ${theme.fontFamilies.monospace}`,
  };

  theme.fontFamilies = fontFamilies;

  /* after override fontFamilies, we still need to override fontFamily used in theme.fontStyles,
 for example: in the fontStyles file, theme.fontStyles.base.fontFamily and theme.fontStyles.baseHeader.fontFamily are using
 theme.fontFamilies.base, so when override, we also use new defined theme.fontFamilies.base
  */

  theme.fontStyles.base.fontFamily = theme.fontFamilies.base;
  theme.fontStyles.baseHeader.fontFamily = theme.fontFamilies.base;
  theme.fontStyles.lead.fontFamily = theme.fontFamilies.serif;
  theme.fontStyles.listLabel.fontFamily = theme.fontFamilies.serif;
  theme.fontStyles.tableHeader.fontFamily = theme.fontFamilies.serif;
};

export const handleFontFamilies = (locale: string, theme: Theme) => {
  switch (locale) {
    case 'lv':
    case 'lt':
      overrideFontFamilies('Poppins', theme);
      break;
    default:
  }
};
